/**
 * Backup Retention API client (Spec 201 — US1)
 *
 * GFS policies, impact-preview, recycle bin (list/restore/purge), per-backup legal hold.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */

import apiClient from '../services/api';

export type BackupEntityType = 'lakehouse' | 'warehouse';

export interface GfsRetentionPolicy {
  id: string;
  name: string;
  isDefault: boolean;
  keepFull: number | null;
  keepIncremental: number | null;
  gfsDaily: number | null;
  gfsWeekly: number | null;
  gfsMonthly: number | null;
  gfsYearly: number | null;
  graceDays: number;
  appliesToEntityType: BackupEntityType | null;
}

export interface RecycleBinEntry {
  entityType: BackupEntityType;
  entityId: string;
  runId: string;
  pointTime: string;
  backupType: 'full' | 'incremental';
  sizeBytes: number | null;
  tableCount: number | null;
  legalHold: boolean;
  storageLegalHold?: boolean; // Spec 214 FR-030: storage-level hold also blocks purge
  hardDeleteAfter: string | null;
  hasManifest?: boolean; // Spec 214 FR-001: false = blob location unknown, purge disabled up front
}

export interface RetentionDecision {
  id: string;
  action: 'keep' | 'prune';
  reason: string;
}

type Wrapped<T> = { success: boolean; data: T };
const BASE = '/api/v1/backup';

export const retentionApi = {
  listPolicies: async (): Promise<GfsRetentionPolicy[]> =>
    (await apiClient.get<Wrapped<GfsRetentionPolicy[]>>(`${BASE}/retention-policies`)).data,

  createPolicy: async (input: Partial<GfsRetentionPolicy>): Promise<GfsRetentionPolicy> =>
    (await apiClient.post<Wrapped<GfsRetentionPolicy>>(`${BASE}/retention-policies`, input)).data,

  updatePolicy: async (id: string, input: Partial<GfsRetentionPolicy>): Promise<GfsRetentionPolicy> =>
    (await apiClient.put<Wrapped<GfsRetentionPolicy>>(`${BASE}/retention-policies/${id}`, input)).data,

  impactPreview: async (
    entityType: BackupEntityType,
    entityId: string,
    policyId?: string
  ): Promise<{ policySource: string; prunedCount: number; decisions: RetentionDecision[] }> =>
    (
      await apiClient.post<Wrapped<{ policySource: string; prunedCount: number; decisions: RetentionDecision[] }>>(
        `${BASE}/retention-policies/impact-preview`,
        { entityType, entityId, policyId }
      )
    ).data,

  listRecycleBin: async (): Promise<RecycleBinEntry[]> =>
    (await apiClient.get<Wrapped<RecycleBinEntry[]>>(`${BASE}/recycle-bin`)).data,

  restoreFromBin: async (entityType: BackupEntityType, runId: string): Promise<{ restored: boolean }> =>
    (await apiClient.post<Wrapped<{ restored: boolean }>>(`${BASE}/recycle-bin/${entityType}/${runId}/restore`)).data,

  /** Spec 214 FR-001: the manifest is resolved SERVER-SIDE — no client manifestPath (the old
   *  optional param was never suppliable, which silently skipped blob deletion). */
  purge: async (entityType: BackupEntityType, runId: string): Promise<void> => {
    await apiClient.delete(`${BASE}/recycle-bin/${entityType}/${runId}`);
  },

  setRunLegalHold: async (entityType: BackupEntityType, runId: string, hold: boolean): Promise<{ updated: boolean }> =>
    (await apiClient.post<Wrapped<{ updated: boolean }>>(`${BASE}/runs/${entityType}/${runId}/legal-hold`, { hold })).data,

  /** Assign a retention policy at the SOURCE (null = inherit the default for the entity type). */
  setEntityRetention: async (entityType: BackupEntityType, entityId: string, retentionPolicyId: string | null): Promise<void> => {
    await apiClient.put(`${BASE}/entities/${entityType}/${entityId}/retention`, { retentionPolicyId });
  },
};
